import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  Tv, Zap, Play, CheckCircle, Clock, Shield, Star, 
  MessageCircle, Copy, Loader2, X, Sparkles, Wifi,
  Monitor, Smartphone, Globe, Award, CreditCard, AlertTriangle
} from 'lucide-react';

export default function PublicStorePage() {
  const { storeSlug } = useParams();
  const [loading, setLoading] = useState(true);
  const [store, setStore] = useState(null);
  const [error, setError] = useState(null);
  
  // Modal states
  const [showTrialModal, setShowTrialModal] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [formData, setFormData] = useState({ name: '', phone: '', email: '' });
  const [submitting, setSubmitting] = useState(false);
  const [trialCredentials, setTrialCredentials] = useState(null);
  const [paymentData, setPaymentData] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchStore();
  }, [storeSlug]);

  const fetchStore = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/public/store/${storeSlug}`);
      if (!response.ok) {
        throw new Error('Loja não encontrada');
      }
      const data = await response.json();
      setStore(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Formatar telefone com DDI+DDD
  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 4) return `+${numbers.slice(0, 2)} (${numbers.slice(2)}`;
    if (numbers.length <= 6) return `+${numbers.slice(0, 2)} (${numbers.slice(2, 4)}) ${numbers.slice(4)}`;
    if (numbers.length <= 11) return `+${numbers.slice(0, 2)} (${numbers.slice(2, 4)}) ${numbers.slice(4, 9)}-${numbers.slice(9)}`;
    return `+${numbers.slice(0, 2)} (${numbers.slice(2, 4)}) ${numbers.slice(4, 9)}-${numbers.slice(9, 13)}`;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhone(e.target.value);
    setFormData({ ...formData, phone: formatted });
  };

  const handleTrialSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone) {
      toast.error('Preencha nome e telefone');
      return;
    }

    const phoneNumbers = formData.phone.replace(/\D/g, '');
    if (phoneNumbers.length < 12) {
      toast.error('Telefone inválido. Use DDI + DDD + número');
      return;
    }

    try {
      setSubmitting(true);
      const response = await fetch(`${API_URL}/api/public/store/${storeSlug}/trial`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          phone: phoneNumbers
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        if (data.detail?.includes('já solicitou')) {
          setErrorMessage('Você já solicitou um teste nesta loja. Cada número pode solicitar apenas 1 teste.');
          setShowTrialModal(false);
          setShowErrorModal(true);
          return;
        }
        throw new Error(data.detail || 'Erro ao solicitar teste');
      }

      setTrialCredentials(data);
      setShowTrialModal(false);
      setTimeout(() => setShowCredentialsModal(true), 300);
      toast.success('Teste criado com sucesso!');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePurchaseSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone) {
      toast.error('Preencha nome e telefone');
      return;
    }

    const phoneNumbers = formData.phone.replace(/\D/g, '');
    if (phoneNumbers.length < 12) {
      toast.error('Telefone inválido. Use DDI + DDD + número');
      return;
    }

    try {
      setSubmitting(true);
      const response = await fetch(`${API_URL}/api/public/store/${storeSlug}/purchase/${selectedPlan.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          phone: phoneNumbers
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Erro ao iniciar compra');
      }

      setPaymentData(data);
      setShowPurchaseModal(false);
      setTimeout(() => setShowPaymentModal(true), 300);
      
      startPaymentCheck(data.transaction_id);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const startPaymentCheck = (transactionId) => {
    setCheckingPayment(true);
    const interval = setInterval(async () => {
      try {
        const response = await fetch(`${API_URL}/api/public/store/${storeSlug}/payment/${transactionId}`);
        const data = await response.json();
        
        if (data.status === 'approved') {
          clearInterval(interval);
          setCheckingPayment(false);
          setShowPaymentModal(false);
          
          if (data.credentials) {
            setTrialCredentials({
              ...data.credentials,
              message: 'Pagamento confirmado! Aqui estão seus dados de acesso:'
            });
            setTimeout(() => setShowCredentialsModal(true), 300);
          }
          toast.success('Pagamento confirmado!');
        }
      } catch (err) {
        console.error('Erro ao verificar pagamento:', err);
      }
    }, 5000);

    setTimeout(() => {
      clearInterval(interval);
      setCheckingPayment(false);
    }, 30 * 60 * 1000);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado!');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="text-center animate-fade-in">
          <Loader2 className="w-12 h-12 animate-spin text-emerald-500 mx-auto mb-4" />
          <p className="text-white animate-pulse">Carregando loja...</p>
        </div>
      </div>
    );
  }

  if (error || !store) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="text-center animate-bounce-in">
          <Tv className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Loja não encontrada</h1>
          <p className="text-gray-400">Verifique o link e tente novamente</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen"
      style={{ 
        backgroundColor: store.background_color,
        color: store.text_color
      }}
    >
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.9); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes bounceIn {
          0% { opacity: 0; transform: scale(0.3); }
          50% { transform: scale(1.05); }
          70% { transform: scale(0.9); }
          100% { opacity: 1; transform: scale(1); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        .animate-fade-in { animation: fadeIn 0.5s ease-out; }
        .animate-slide-up { animation: slideUp 0.6s ease-out; }
        .animate-slide-down { animation: slideDown 0.6s ease-out; }
        .animate-scale-in { animation: scaleIn 0.4s ease-out; }
        .animate-bounce-in { animation: bounceIn 0.6s ease-out; }
        .animate-float { animation: float 3s ease-in-out infinite; }
        .animate-delay-100 { animation-delay: 0.1s; }
        .animate-delay-200 { animation-delay: 0.2s; }
        .animate-delay-300 { animation-delay: 0.3s; }
        .animate-delay-400 { animation-delay: 0.4s; }
        .shimmer {
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent);
          background-size: 200% 100%;
          animation: shimmer 2s infinite;
        }
        .hover-lift { transition: transform 0.3s, box-shadow 0.3s; }
        .hover-lift:hover { transform: translateY(-5px); box-shadow: 0 20px 40px rgba(0,0,0,0.3); }
      `}</style>

      {/* Hero Section */}
      <div 
        className="relative overflow-hidden"
        style={{
          background: store.banner_url 
            ? `linear-gradient(to bottom, rgba(0,0,0,0.7), ${store.background_color}), url(${store.banner_url}) center/cover`
            : `linear-gradient(135deg, ${store.primary_color}20, ${store.background_color})`
        }}
      >
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div 
            className="absolute w-96 h-96 rounded-full blur-3xl opacity-20 -top-48 -left-48 animate-float"
            style={{ backgroundColor: store.primary_color }}
          />
          <div 
            className="absolute w-96 h-96 rounded-full blur-3xl opacity-20 -bottom-48 -right-48 animate-float animate-delay-200"
            style={{ backgroundColor: store.accent_color }}
          />
        </div>

        <div className="relative z-10 container mx-auto px-4 py-16">
          {/* Logo */}
          <div className="flex justify-center mb-8 animate-slide-down">
            {store.logo_url ? (
              <img 
                src={store.logo_url} 
                alt={store.store_name}
                className="h-20 object-contain hover-lift"
              />
            ) : (
              <div 
                className="w-20 h-20 rounded-2xl flex items-center justify-center hover-lift animate-float"
                style={{ backgroundColor: store.primary_color }}
              >
                <Tv className="w-10 h-10 text-white" />
              </div>
            )}
          </div>

          {/* Title */}
          <h1 className="text-4xl md:text-6xl font-bold text-center mb-4 animate-slide-up">
            {store.store_name}
          </h1>
          
          <p className="text-xl text-center opacity-80 max-w-2xl mx-auto mb-8 animate-slide-up animate-delay-100">
            {store.description}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-slide-up animate-delay-200">
            {store.show_trial && (
              <button
                onClick={() => setShowTrialModal(true)}
                className="group px-8 py-4 rounded-xl font-bold text-lg flex items-center gap-3 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl hover-lift"
                style={{ 
                  backgroundColor: store.accent_color,
                  color: '#000'
                }}
              >
                <Zap className="w-6 h-6 group-hover:animate-pulse" />
                TESTE GRÁTIS {store.trial_duration || '24H'}
                <Sparkles className="w-5 h-5" />
              </button>
            )}
            
            <button
              onClick={() => document.getElementById('plans')?.scrollIntoView({ behavior: 'smooth' })}
              className="px-8 py-4 rounded-xl font-bold text-lg flex items-center gap-3 transition-all duration-300 border-2 hover:scale-105 hover-lift"
              style={{ 
                borderColor: store.primary_color,
                color: store.text_color
              }}
            >
              <Play className="w-6 h-6" />
              VER PLANOS
            </button>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid md:grid-cols-4 gap-6">
          {[
            { icon: Monitor, title: 'Smart TV', desc: 'Compatível com todas Smart TVs' },
            { icon: Smartphone, title: 'Mobile', desc: 'Assista no celular ou tablet' },
            { icon: Globe, title: '+10.000 Canais', desc: 'Canais de todo o mundo' },
            { icon: Shield, title: 'Suporte 24h', desc: 'Atendimento dedicado' },
          ].map((feature, i) => (
            <div 
              key={i}
              className={`p-6 rounded-2xl text-center transition-all duration-300 hover-lift animate-slide-up`}
              style={{ 
                backgroundColor: `${store.primary_color}15`,
                animationDelay: `${i * 0.1}s`
              }}
            >
              <feature.icon 
                className="w-12 h-12 mx-auto mb-4"
                style={{ color: store.primary_color }}
              />
              <h3 className="font-bold text-lg mb-2">{feature.title}</h3>
              <p className="text-sm opacity-70">{feature.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Plans Section */}
      <div id="plans" className="container mx-auto px-4 py-16">
        <div className="text-center mb-12 animate-slide-up">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Escolha seu Plano</h2>
          <p className="text-lg opacity-70">Selecione o plano ideal para você</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {store.plans?.map((plan, index) => (
            <div
              key={plan.id}
              className={`relative rounded-2xl p-6 transition-all duration-500 hover-lift animate-slide-up ${index === 1 ? 'md:scale-105' : ''}`}
              style={{ 
                backgroundColor: index === 1 ? store.primary_color : `${store.primary_color}15`,
                color: index === 1 ? '#fff' : store.text_color,
                animationDelay: `${index * 0.15}s`
              }}
            >
              {index === 1 && (
                <div 
                  className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold flex items-center gap-1 animate-bounce-in"
                  style={{ backgroundColor: store.accent_color, color: '#000' }}
                >
                  <Star className="w-3 h-3" /> MAIS POPULAR
                </div>
              )}

              <div className="text-center mb-6">
                <h3 className="text-xl font-bold mb-2">{plan.name}</h3>
                <p className="text-sm opacity-70">{plan.package_name}</p>
              </div>

              <div className="text-center mb-6">
                <span className="text-4xl font-bold">
                  R$ {plan.price?.toFixed(2)}
                </span>
                <span className="text-sm opacity-70">/{plan.duration_days} dias</span>
              </div>

              <ul className="space-y-3 mb-6">
                {[
                  'Todos os canais inclusos',
                  'Qualidade Full HD/4K',
                  'Suporte técnico',
                  'Guia de programação',
                ].map((feature, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: index === 1 ? '#fff' : store.primary_color }} />
                    {feature}
                  </li>
                ))}
              </ul>

              <button
                onClick={() => {
                  if (!store.has_gateway) {
                    toast.error('Pagamento não disponível. Entre em contato pelo WhatsApp.');
                    return;
                  }
                  setSelectedPlan(plan);
                  setShowPurchaseModal(true);
                }}
                className="w-full py-3 rounded-xl font-bold transition-all duration-300 hover:opacity-90 hover:scale-105"
                style={{ 
                  backgroundColor: index === 1 ? '#fff' : store.accent_color,
                  color: index === 1 ? store.primary_color : '#000'
                }}
              >
                ASSINAR AGORA
              </button>
            </div>
          ))}
        </div>

        {store.plans?.length === 0 && (
          <div className="text-center py-12 animate-fade-in">
            <Tv className="w-16 h-16 mx-auto mb-4 opacity-50" />
            <p className="text-lg opacity-70">Nenhum plano disponível no momento</p>
          </div>
        )}
      </div>

      {/* WhatsApp Button */}
      {store.whatsapp_number && (
        <a
          href={`https://wa.me/${store.whatsapp_number}`}
          target="_blank"
          rel="noopener noreferrer"
          className="fixed bottom-6 right-6 w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 hover:scale-110 z-50 animate-bounce-in"
          style={{ backgroundColor: '#25D366' }}
        >
          <MessageCircle className="w-7 h-7 text-white" />
        </a>
      )}

      {/* Footer */}
      <footer 
        className="py-8 text-center animate-fade-in"
        style={{ backgroundColor: `${store.primary_color}10` }}
      >
        <p className="text-sm opacity-70">{store.footer_text}</p>
      </footer>

      {/* Trial Modal */}
      {showTrialModal && (
        <Modal onClose={() => setShowTrialModal(false)} store={store}>
          <div className="text-center mb-6 animate-bounce-in">
            <div 
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ backgroundColor: store.accent_color }}
            >
              <Zap className="w-8 h-8 text-black" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Teste Grátis</h2>
            <p className="opacity-70">Aproveite {store.trial_duration || '24H'} gratuitos!</p>
          </div>

          <form onSubmit={handleTrialSubmit} className="space-y-4 animate-slide-up animate-delay-100">
            <div>
              <label className="block text-sm font-medium mb-1">Seu Nome</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-3 rounded-lg bg-black/30 border border-white/20 focus:border-white/50 outline-none transition-all duration-300"
                placeholder="Digite seu nome"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">WhatsApp (com DDI)</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={handlePhoneChange}
                className="w-full px-4 py-3 rounded-lg bg-black/30 border border-white/20 focus:border-white/50 outline-none transition-all duration-300"
                placeholder="+55 (11) 99999-9999"
                required
              />
              <p className="text-xs opacity-50 mt-1">Ex: +55 (11) 99999-9999</p>
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="w-full py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-2 transition-all duration-300 hover:opacity-90 hover:scale-105 disabled:opacity-50"
              style={{ backgroundColor: store.accent_color, color: '#000' }}
            >
              {submitting ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Zap className="w-5 h-5" />
                  LIBERAR TESTE
                </>
              )}
            </button>
          </form>
        </Modal>
      )}

      {/* Error Modal (já solicitou teste) */}
      {showErrorModal && (
        <Modal onClose={() => setShowErrorModal(false)} store={store}>
          <div className="text-center animate-bounce-in">
            <div 
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ backgroundColor: '#ef4444' }}
            >
              <AlertTriangle className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Ops!</h2>
            <p className="opacity-70 mb-6">{errorMessage}</p>
            
            {store.whatsapp_number && (
              <a
                href={`https://wa.me/${store.whatsapp_number}?text=Olá! Gostaria de saber mais sobre os planos.`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full py-3 rounded-xl font-bold flex items-center justify-center gap-2 transition-all duration-300 hover:opacity-90"
                style={{ backgroundColor: '#25D366' }}
              >
                <MessageCircle className="w-5 h-5" />
                Falar no WhatsApp
              </a>
            )}
          </div>
        </Modal>
      )}

      {/* Purchase Modal */}
      {showPurchaseModal && selectedPlan && (
        <Modal onClose={() => setShowPurchaseModal(false)} store={store}>
          <div className="text-center mb-6 animate-bounce-in">
            <h2 className="text-2xl font-bold mb-2">{selectedPlan.name}</h2>
            <p className="text-3xl font-bold" style={{ color: store.accent_color }}>
              R$ {selectedPlan.price?.toFixed(2)}
            </p>
          </div>

          <form onSubmit={handlePurchaseSubmit} className="space-y-4 animate-slide-up animate-delay-100">
            <div>
              <label className="block text-sm font-medium mb-1">Seu Nome</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-3 rounded-lg bg-black/30 border border-white/20 focus:border-white/50 outline-none transition-all duration-300"
                placeholder="Digite seu nome"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">WhatsApp (com DDI)</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={handlePhoneChange}
                className="w-full px-4 py-3 rounded-lg bg-black/30 border border-white/20 focus:border-white/50 outline-none transition-all duration-300"
                placeholder="+55 (11) 99999-9999"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">E-mail (opcional)</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-3 rounded-lg bg-black/30 border border-white/20 focus:border-white/50 outline-none transition-all duration-300"
                placeholder="seu@email.com"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="w-full py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-2 transition-all duration-300 hover:opacity-90 hover:scale-105 disabled:opacity-50"
              style={{ backgroundColor: store.primary_color }}
            >
              {submitting ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Play className="w-5 h-5" />
                  GERAR PIX
                </>
              )}
            </button>
          </form>
        </Modal>
      )}

      {/* Payment Modal */}
      {showPaymentModal && paymentData && (
        <Modal onClose={() => setShowPaymentModal(false)} store={store} large>
          <div className="text-center mb-6 animate-bounce-in">
            <h2 className="text-2xl font-bold mb-2">Pagamento via PIX</h2>
            <p className="text-3xl font-bold" style={{ color: store.accent_color }}>
              R$ {paymentData.amount?.toFixed(2)}
            </p>
          </div>

          <div className="flex flex-col items-center gap-4 animate-slide-up animate-delay-100">
            {paymentData.qr_code && (
              <div className="bg-white p-4 rounded-xl animate-scale-in">
                <img 
                  src={paymentData.qr_code ? (paymentData.qr_code.startsWith('data:') ? paymentData.qr_code : paymentData.qr_code.startsWith('http') ? paymentData.qr_code : `data:image/png;base64,${paymentData.qr_code}`) : (paymentData.qr_code_url || (paymentData.qr_code_text ? `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(paymentData.qr_code_text)}` : ''))}
                  alt="QR Code PIX"
                  className="w-48 h-48"
                />
              </div>
            )}

            <div className="w-full">
              <label className="block text-sm font-medium mb-2 text-center">Código Copia e Cola:</label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={paymentData.qr_code_text}
                  readOnly
                  className="flex-1 px-4 py-3 rounded-lg bg-black/30 border border-white/20 text-sm font-mono"
                />
                <button
                  onClick={() => copyToClipboard(paymentData.qr_code_text)}
                  className="px-4 py-3 rounded-lg transition-all duration-300 hover:opacity-80 hover:scale-105"
                  style={{ backgroundColor: store.primary_color }}
                >
                  <Copy className="w-5 h-5" />
                </button>
              </div>
            </div>

            {checkingPayment && (
              <div className="flex items-center gap-2 text-sm opacity-70 animate-pulse">
                <Loader2 className="w-4 h-4 animate-spin" />
                Aguardando pagamento...
              </div>
            )}

            <p className="text-xs opacity-50 text-center">
              Após o pagamento, suas credenciais serão exibidas automaticamente
            </p>
          </div>
        </Modal>
      )}

      {/* Credentials Modal */}
      {showCredentialsModal && trialCredentials && (
        <Modal onClose={() => setShowCredentialsModal(false)} store={store}>
          <div className="text-center mb-6 animate-bounce-in">
            <div 
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 animate-scale-in"
              style={{ backgroundColor: store.primary_color }}
            >
              <CheckCircle className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Acesso Liberado!</h2>
            <p className="opacity-70">{trialCredentials.message || `Aproveite seu acesso!`}</p>
          </div>

          <div className="space-y-4 animate-slide-up animate-delay-100">
            <div className="p-4 rounded-lg transition-all duration-300 hover:scale-105" style={{ backgroundColor: `${store.primary_color}20` }}>
              <label className="block text-sm font-medium mb-1">Usuário:</label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={trialCredentials.username}
                  readOnly
                  className="flex-1 px-3 py-2 rounded bg-black/30 font-mono"
                />
                <button
                  onClick={() => copyToClipboard(trialCredentials.username)}
                  className="p-2 rounded transition-all duration-300 hover:opacity-80 hover:scale-110"
                  style={{ backgroundColor: store.primary_color }}
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Info: credenciais completas no WhatsApp */}
            <div className="p-4 rounded-lg text-center" style={{ backgroundColor: `${store.accent_color}20`, borderColor: store.accent_color, borderWidth: 1 }}>
              <MessageCircle className="w-8 h-8 mx-auto mb-2" style={{ color: store.accent_color }} />
              <p className="font-medium" style={{ color: store.accent_color }}>
                A senha e demais informações foram enviadas para seu WhatsApp!
              </p>
            </div>

            {(trialCredentials.duration || trialCredentials.expiration) && (
              <div className="flex items-center justify-center gap-2 text-sm opacity-70">
                <Clock className="w-4 h-4" />
                {trialCredentials.duration && <span>Válido por {trialCredentials.duration}</span>}
                {trialCredentials.expiration && <span> - Expira: {trialCredentials.expiration}</span>}
              </div>
            )}
          </div>

          {store.whatsapp_number && (
            <a
              href={`https://wa.me/${store.whatsapp_number}?text=Olá! Acabei de criar meu acesso: ${trialCredentials.username}`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 w-full py-3 rounded-xl font-bold flex items-center justify-center gap-2 transition-all duration-300 hover:opacity-90 hover:scale-105"
              style={{ backgroundColor: '#25D366' }}
            >
              <MessageCircle className="w-5 h-5" />
              Falar no WhatsApp
            </a>
          )}
        </Modal>
      )}
    </div>
  );
}

// Modal Component with animations
function Modal({ children, onClose, store, large = false }) {
  const [isClosing, setIsClosing] = useState(false);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${isClosing ? 'animate-fade-out' : 'animate-fade-in'}`}>
      <style>{`
        @keyframes fade-out { from { opacity: 1; } to { opacity: 0; } }
        .animate-fade-out { animation: fade-out 0.3s ease-out forwards; }
      `}</style>
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={handleClose} />
      <div 
        className={`relative w-full ${large ? 'max-w-lg' : 'max-w-md'} rounded-2xl p-6 shadow-2xl ${isClosing ? 'animate-scale-out' : 'animate-scale-in'}`}
        style={{ backgroundColor: store.background_color, color: store.text_color }}
      >
        <style>{`
          @keyframes scale-out { from { opacity: 1; transform: scale(1); } to { opacity: 0; transform: scale(0.9); } }
          .animate-scale-out { animation: scale-out 0.3s ease-out forwards; }
        `}</style>
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/10 transition-all duration-300 hover:rotate-90"
        >
          <X className="w-5 h-5" />
        </button>
        {children}
      </div>
    </div>
  );
}
